import { useParams } from "react-router";
import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { FindingList } from "@/components/patterns/FindingList";
import { Panel } from "@/components/ui/Panel";
import { useClaimSetWritable } from "@/hooks/use-claim-set-writable";
import { useClaimStrategy } from "@/hooks/use-claim-strategy";
import { ClaimabilityNote } from "../claim-strategy/ClaimabilityNote";
import { POSITIONS_UNSETTLED } from "../claim-strategy/ClaimStrategyGuard";
import { ClaimSet } from "../claim-strategy/ClaimSet";
import {
  ProposalState,
  awaitingFirstProposal,
} from "../claim-strategy/ProposalState";
import { MaybeLink } from "../layout/MaybeLink";
import { StagePage } from "../layout/StagePage";
import { stageHeadingLabel, tabPath } from "../stage-vocabulary";
import { ReadProblem } from "@/components/patterns/ReadProblem";
import { StageSources } from "@/v2/layout/StageSources";

const LEDE =
  "The positions you defined during the Novelty phase form the basis of the claims shown on this screen. " +
  "The assistant has placed the positions you agreed into a claim set and tagged each claim with its category. " +
  "Review it, retag or reorder claims, and lock.";

/** Why the stage is read-only while a position is not settled, and the way back to the analysis. */
function ShutNotice({ taskId }: { taskId: string }) {
  return (
    <Panel role="status" tone="warning" className="mt-6 text-sm">
      {POSITIONS_UNSETTLED}: a position changed after you confirmed it, so this
      set is shown read-only.{" "}
      <MaybeLink
        to={tabPath(taskId, "thesis", "novelty", "analysis")}
        className="font-semibold underline"
      >
        Go to the novelty analysis
      </MaybeLink>
    </Panel>
  );
}

function Stage({
  taskId,
  stage,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
}) {
  const writable = useClaimSetWritable(taskId);
  return (
    <>
      {!writable && <ShutNotice taskId={taskId} />}
      <ClaimabilityNote claimability={stage.claimability} />
      <ProposalState taskId={taskId} stage={stage} />
      {!awaitingFirstProposal(stage) && (
        <>
          {/* What the last run raised on the set as a whole, on no one claim. */}
          <FindingList
            findings={stage.set_findings}
            testId="set-finding"
            className="mt-6"
          />
          <ClaimSet taskId={taskId} stage={stage} />
        </>
      )}
    </>
  );
}

function OfTask({ taskId }: { taskId: string }) {
  const query = useClaimStrategy(taskId);
  const { data } = query;
  return (
    <StagePage
      guide="claim-strategy"
      testId="claim-strategy-pane"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("claim-strategy")} · Claim strategy`}
      title="Build the claim set"
      lede={LEDE}
    >
      <StageSources taskId={taskId} stage="claim_strategy" />
      {/* Beside the set, never in place of it: a failed read again would otherwise
          unmount every claim field while the attorney is typing into it. */}
      <ReadProblem query={query} what="the claim set" className="mt-6" />
      {data !== undefined ? (
        <Stage taskId={taskId} stage={data} />
      ) : (
        !query.isError && (
          <p className="mt-6 text-sm text-muted-foreground">
            Reading the claim set…
          </p>
        )
      )}
    </StagePage>
  );
}

/** Stage 4. Zero props by contract: the pane reads its own route params. */
export function ClaimStrategyStagePane() {
  const { taskId } = useParams<{ taskId: string }>();
  return taskId === undefined ? null : <OfTask taskId={taskId} />;
}
